import { Component, OnInit, computed, inject, model, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import {
    DialogService, GameEngineService,
    GameService, InternationalizationService,
    PlaySoundsService,
    SessionService,
    TransitionService,
    User
} from '@app/core/services';
import { DialogConfig, GameErrorComponent } from '@app/shared/game-error/game-error.component';
import { AddJeopardyGameInput, JeopardyGame, JeopardyUser, UpdateJeopardyUserInput } from '@app/shared/graphql/types';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import pack from "../../../../package.json";
import { SystemService } from "../../shared/rest-api";
import { AvatarCarouselComponent } from '../avatar-carousel/avatar-carousel.component';

@Component({
    selector: 'app-main-menu',
    templateUrl: './main-menu.component.html',
    styleUrls: ['./main-menu.component.scss'],
    animations: [],
    imports: [AvatarCarouselComponent, RouterLink]
})
export class MainMenuComponent implements OnInit {
  public changeAvatarActive = model(false);

  public currentUser = signal<User>(null);
  public gameInProgress = signal(false);
  public game = signal<JeopardyGame>(null);
  public soundsOff = signal(true);
  public musicOff = signal(true);
  public avatars = signal<string[]>([]);
  protected avatarSelected = signal<string>('');

  public getRoundSub: Subscription;

  public i18n = inject(InternationalizationService);
  public systemService = inject(SystemService);

  private currentDevice = signal<string[]>(null);
  private gameCreated$: BehaviorSubject<number> = new BehaviorSubject(null);
  private gameInitialized$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private userDetails$: BehaviorSubject<JeopardyUser> = new BehaviorSubject(null);

  private inactiveGameSub: Subscription;
  private router = inject(Router);
  private sessionService = inject(SessionService);
  private playSoundsService = inject(PlaySoundsService);
  private transitionService = inject(TransitionService);
  private gameService = inject(GameService);
  private dialogService = inject(DialogService);
  private gameEngineService = inject(GameEngineService);

  private backendInfo = toSignal(this.systemService.apiVersionGet());
  private graphQLInfo = toSignal(this.gameService.getVersion());
  private graphQLVersion = computed(() => {
    const version = this.graphQLInfo()?.version ?? null;
    if(version){
        return `| GraphQL ${version.version}`
    }
    return '';
  });
  frontendVersionNumber = pack.version;
  versionNumber = computed(
    () =>
      `ChallengEU ${this.frontendVersionNumber} | API ${
        this.backendInfo()?.version ?? ""
      } ${this.graphQLVersion()}`
  );

  ngOnInit(): void {
    this.avatars.update(() => [ ...Array(15).keys() ].map(e => `assets/app-branding/img/avatars/cartoon/${e+1}.png`));
    // this.transitionService.showGameLoader(this.i18n.str('challengeu.transition_starting_game', "Starting Game")());

    // this.gameInProgress = false;

    const session = this.sessionService.getSession();
    this.soundsOff.set(session?.soundsOff ?? false);
    this.musicOff.set(session?.musicOff ?? false);
    this.currentUser.set(this.sessionService.getUser());
    this.currentDevice.set(this.gameService.getDeviceDetails());

    if (!this.musicOff()) {
      this.playSoundsService.playMusic('settings');
    }

    // check if game active
    const gameId = +this.gameService.getGameId();
    if(gameId) {
      this.inactiveGameSub = this.gameService.getGameInactivatedSubscription(gameId)?.subscribe(() => {
        console.log('game inactivated');
        this.finishGame();
        this.inactiveGameSub?.unsubscribe();
      });

      this.gameService.getGameDetails(gameId).pipe(
        take(1),
        tap(g =>{
          if(!g) {
            return;
          }
          if(!g.active) {
            this.finishGame();
            return;
          }
  
          this.game.set(g);
          this.gameInProgress.set(true);
        })
      )?.subscribe();
    }

    // init session game after creation
    this.gameCreated$.asObservable()
    .pipe(
      filter(gameId => !!gameId),
      switchMap(gameId => this.gameEngineService.init(gameId)),
      take(1)
    )?.subscribe((game) => {
      this.sessionService.clearSession();
      this.gameService.setGameId(game?.gameId);
      this.currentUser.set(this.sessionService.updateUserGameId(game?.gameId));
      this.gameInitialized$?.next(true);
    });

    // get user details after initialization
    this.gameInitialized$.asObservable()
    .pipe(
      filter(done => done),
      switchMap(() => this.gameService.getUserById(this.gameEngineService.connectedPlayer?.userId)),
      tap((user) => {
        this.sessionService.updateUserScore(user?.score);
        this.userDetails$?.next(user);
      }),
      take(1)
    )?.subscribe();

    // update user
    this.userDetails$.asObservable()
    .pipe(
      filter(u => !!u),
      map(u => {
        const avatar = this.currentUser()?.avatar?.split('/')?.pop();
        const userUpdate: UpdateJeopardyUserInput = {
          userId: this.gameEngineService.connectedPlayer?.userId,
          connected: true,
          language: this.currentUser()?.location.language?.isoCode2Letters,
          location: this.currentUser()?.location.country?.value,
          deviceDetected: this.currentDevice()[0],
          deviceIdentifier: this.currentDevice()[1],
          name: this.currentUser()?.username,
          jokerFiftyFifty: u?.jokerFiftyFifty,
          jokerSkip: u?.jokerSkip,
          playInactivityCounter: u?.playInactivityCounter,
          score: u?.score,
          avatar: avatar,
          host: false
        };
        
        return userUpdate;
      }),
      switchMap((user) => this.gameService.updateUser(user)),
      take(1)
    )?.subscribe(
      () => {
        timer(1000).pipe(
          map(() => {
            this.transitionService.hideGameLoader();
            this.router.navigate(['select-topic']);
          }),
          take(1)
        )?.subscribe();
      }
    );
  }

  private startGame(params: AddJeopardyGameInput) {
    this.transitionService.showGameLoader(this.i18n.str('challengeu.transition_starting_game', "Starting Game")());
    this.gameService.createGame(params)
    .pipe(
      take(1)
    )?.subscribe(
      (game) => {
        this.gameCreated$?.next(game?.gameId);
      },
      error => {
        this.transitionService.hideGameLoader();
        console.log(error);
      }
    );
  }

  protected selectModeSP = (ev: MouseEvent, isAdvancedMode: boolean) => {
    ev.preventDefault();

    if(!this.playSoundsService.soundsOff) {
      this.playSoundsService.playSound('primary-button');
    }

    // check if game already in progress
    if(this.gameInProgress()) {
      if(!this.playSoundsService.soundsOff) {
        this.playSoundsService.playSound('pop-up');
      }

      const config: DialogConfig = {
        header: this.i18n.str('challengeu.dialog_warning_title', 'Warning')(),
        message: this.i18n.str('challengeu.dialog_game_in_progress', "You already have a game in progress.")(),
        acceptButtonLabel: this.i18n.str('challengeu.dialog_continue_game','CONTINUE GAME')(),
        cancelButtonLabel: this.i18n.str('challengeu.dialog_start_new_game','START NEW GAME')(),
      }

      const ref = this.dialogService.open(GameErrorComponent, config);
      ref.afterClosed$?.subscribe(res => {
        if(typeof res?.data === 'undefined') {
          return;
        }

        if(res?.data === true) {
          // continue game
          this.continueGame();
        } else if(res?.data === false) {
          // finish game and start new one
          this.sessionService.updateUserGameId(null);
          this.finishGame();
          
          setTimeout(() => {
            this.createGame(isAdvancedMode);
          }, 300);
        }
      });
      return;
    }

    this.gameEngineService.clearGame();
    this.createGame(isAdvancedMode);
  };

  protected selectModeAM = (ev: MouseEvent) => {
    this.selectModeSP(ev, true);
  };

  private createGame(isAdvancedMode: boolean) {
    const gameInput: AddJeopardyGameInput = {
      multiplayer: false,
      roomName: '',
      langue: this.currentUser()?.location?.language?.isoCode4Letters,
      players: 1,
      private: false,
      advancedMode: isAdvancedMode
    };

    this.startGame(gameInput);
  }
  
  selectModeMP(ev: MouseEvent) {
    ev.preventDefault();

    if(this.gameInProgress && this.game().players === 1) {
      const config: DialogConfig = {
        header: this.i18n.str('challengeu.dialog_warning_title', 'Warning')(),
        message: this.i18n.str('challengeu.dialog_sp_game_in_progress',"You already have a single-player game in progress.")(),
        showAcceptButton: false,
        cancelButtonLabel: this.i18n.str('challengeu.dialog_close','CLOSE')(),
      }

      const ref = this.dialogService.open(GameErrorComponent, config);
      ref.afterClosed$?.subscribe();

      return;
    }

    this.router.navigate(['multiplayer']);
  }

  doMuteMusic(ev: MouseEvent) {
    ev.preventDefault();

    this.playSoundsService.musicOff = !this.playSoundsService.musicOff;
    this.sessionService.setMusicOff(this.playSoundsService.musicOff);
    this.musicOff.set(this.playSoundsService.musicOff);

    if(!this.playSoundsService.musicOff) {
      this.playSoundsService.playMusic('settings');
    } else {
      this.playSoundsService.pauseMusic();
    }
  }

  setSoundsFX(ev: Event) {
    ev.preventDefault();
    this.playSoundsService.soundsOff = !this.playSoundsService.soundsOff;
    this.sessionService.setSoundsOff(this.playSoundsService.soundsOff);
    this.soundsOff.set(this.playSoundsService.soundsOff);
  }

  public continueGame(ev: Event = null) {
    if(ev) {
      ev.preventDefault();
    }

    const gameId = +this.gameService.getGameId();

    // TODO: check game state (select topic, finished) for MP and Singleplayer;

    if(this.gameEngineService.isMultiplayer) {      
      this.router.navigate(['multiplayer', 'lobby', gameId]);
    } else {
      this.getRoundSub = this.gameService.getGameRound(gameId).subscribe(round => {
        if(round?.timeStart && !round.timeEnd) {
          setTimeout(() => {
            this.router.navigate(['question']);
          }, 500);
        } else {
          this.transitionService.showGameLoader('Loading game');
          setTimeout(() => {
            this.router.navigate(['select-topic']);
            setTimeout(() => {
              this.transitionService.hideGameLoader();
            }, 500)
          }, 500);
        }
      });
    }
  }

  public finishGame(ev: Event = null) {    
    if(ev) {
      ev.preventDefault();
    }

    this.gameInProgress.set(false);
    this.sessionService.updateUserGameId(null);
    this.gameService.finishGame();
    this.gameEngineService.clearGame();
    this.inactiveGameSub?.unsubscribe();
  }

  ngOnDestroy() {
    this.playSoundsService?.stopSound();
    this.inactiveGameSub?.unsubscribe();
  }
}
